export interface WebMcpTool {
  name: string;
  title?: string;
  description: string;
  inputSchema?: Record<string, unknown>;
  annotations?: {
    readOnlyHint?: boolean;
    untrustedContentHint?: boolean;
  };
  execute: (input: Record<string, unknown>) => Promise<unknown>;
}

export interface WebMcpModelContext {
  registerTool: (
    tool: WebMcpTool,
    options?: { signal?: AbortSignal; exposedTo?: string[] },
  ) => Promise<undefined>;
}

export interface WebMcpDocument {
  modelContext?: WebMcpModelContext;
}

export interface ReportForSummary {
  day_report_id: string;
  project_id?: string;
  report_date: string;
  revision: number;
  status: string;
  team_members?: unknown[];
  work_assignments?: unknown[];
  time_entries?: unknown[];
  equipment?: unknown[];
  transport_entries?: unknown[];
  assets?: unknown[];
}

export type ReportReader = (reportId: string) => Promise<ReportForSummary>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function requireReportId(input: Record<string, unknown>): string {
  const reportId = input.report_id;
  if (typeof reportId !== "string" || !UUID_PATTERN.test(reportId)) {
    throw new TypeError("report_id moet een geldige UUID zijn.");
  }
  return reportId;
}

export function summarizeReport(report: ReportForSummary) {
  return {
    day_report_id: report.day_report_id,
    project_id: report.project_id ?? null,
    report_date: report.report_date,
    revision: report.revision,
    status: report.status,
    counts: {
      team_members: report.team_members?.length ?? 0,
      work_assignments: report.work_assignments?.length ?? 0,
      time_entries: report.time_entries?.length ?? 0,
      equipment: report.equipment?.length ?? 0,
      transport_entries: report.transport_entries?.length ?? 0,
      assets: report.assets?.length ?? 0,
    },
  };
}

export function createReportSummaryTool(readReport: ReportReader): WebMcpTool {
  return {
    name: "dbvdw.get_report_summary",
    title: "Dagrapport samenvatting ophalen",
    description:
      "Haalt voor de ingelogde gebruiker een beperkte samenvatting van een DBVDW-dagrapport op. De bestaande backend blijft autorisatie en toegang afdwingen.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      required: ["report_id"],
      properties: {
        report_id: {
          type: "string",
          format: "uuid",
          maxLength: 36,
          description: "UUID van het dagrapport.",
        },
      },
    },
    annotations: {
      readOnlyHint: true,
      untrustedContentHint: false,
    },
    execute: async (input) => {
      const reportId = requireReportId(input);
      const report = await readReport(reportId);
      return summarizeReport(report);
    },
  };
}

export async function registerWebMcpTools(
  targetDocument: object,
  readReport: ReportReader,
): Promise<boolean> {
  const modelContext = (targetDocument as WebMcpDocument).modelContext;
  if (!modelContext?.registerTool) return false;

  await modelContext.registerTool(createReportSummaryTool(readReport));
  return true;
}
